/**
 * AP-11 — Dateizugriff über die File System Access API.
 * Gegenstück zu `src/ui/FileDialogs.cpp` (`ShowSave`/`ShowOpen`) und zusätzlich
 * der Einstiegspunkt für `file_handlers` aus dem Manifest (Doppelklick auf eine
 * `.trc`/`.asc`/`.dbc`/`.eds`-Datei startet die installierte App).
 *
 * Die App ist ohnehin auf Chromium-Desktop festgelegt (Web Serial, CONTRACTS.md
 * §2.2); deshalb wird hier kein `<input type=file>`-Rückfallweg gebaut, sondern
 * ein klarer Fehler gemeldet, wenn die API fehlt.
 */

import { traceFormatOf, type TraceFormat } from '../trace';

/** Abbruch durch den Nutzer ist kein Fehler — die Aufrufer melden „... canceled". */
const ABORT_ERROR = 'AbortError';

export interface PickedTraceFile {
  handle: FileSystemFileHandle;
  name: string;
  format: TraceFormat;
}

function requirePickerSupport(): void {
  if (!('showSaveFilePicker' in self)) {
    throw new Error('File System Access API not available (Chromium desktop required)');
  }
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === ABORT_ERROR;
}

/** `FileDialogs::ShowSave` — Vorgabename `trace.asc`, Formatwahl über die Endung. */
export async function pickTraceToSave(): Promise<PickedTraceFile | null> {
  requirePickerSupport();
  try {
    const handle = await self.showSaveFilePicker({
      suggestedName: 'trace.asc',
      types: [
        { description: 'Vector ASC', accept: { 'text/plain': ['.asc'] } },
        { description: 'PEAK-style TRC', accept: { 'text/plain': ['.trc'] } },
      ],
    });
    return { handle, name: handle.name, format: traceFormatOf(handle.name) };
  } catch (error) {
    if (isAbort(error)) return null;
    throw error;
  }
}

/** `FileDialogs::ShowOpen` — ein Filter für beide Trace-Formate. */
export async function pickTraceToOpen(): Promise<PickedTraceFile | null> {
  requirePickerSupport();
  try {
    const [handle] = await self.showOpenFilePicker({
      types: [{ description: 'Trace files', accept: { 'text/plain': ['.asc', '.trc'] } }],
    });
    if (handle === undefined) return null;
    return { handle, name: handle.name, format: traceFormatOf(handle.name) };
  } catch (error) {
    if (isAbort(error)) return null;
    throw error;
  }
}

/** Trace-Dateien sind Latin-1/ASCII; `File.text()` würde UTF-8 annehmen. */
export async function readTraceText(handle: FileSystemFileHandle): Promise<string> {
  const buffer = await (await handle.getFile()).arrayBuffer();
  return new TextDecoder('windows-1252').decode(buffer);
}

export type LaunchedFileKind = 'trace' | 'dbc' | 'eds';

export interface LaunchedFile {
  kind: LaunchedFileKind;
  handle: FileSystemFileHandle;
  name: string;
}

function launchedKindOf(name: string): LaunchedFileKind | null {
  const lower = name.toLowerCase();
  if (lower.endsWith('.trc') || lower.endsWith('.asc')) return 'trace';
  if (lower.endsWith('.dbc')) return 'dbc';
  if (lower.endsWith('.eds')) return 'eds';
  return null;
}

/**
 * Nimmt die per `file_handlers` übergebenen Dateien entgegen. Der Consumer darf
 * nur **einmal** gesetzt werden; ohne ihn puffert der Browser den Start-Intent
 * unbegrenzt, deshalb wird er auch dann gesetzt, wenn nichts anliegt.
 */
export function onFilesLaunched(handler: (file: LaunchedFile) => void): void {
  if (!('launchQueue' in self)) return;
  self.launchQueue.setConsumer((params) => {
    for (const handle of params.files) {
      const kind = launchedKindOf(handle.name);
      if (kind !== null) handler({ kind, handle, name: handle.name });
    }
  });
}
